/**
 * Session recovery for the Central admin SPA.
 *
 * WHY THIS EXISTS
 * ---------------
 * The admin SPA had no 401 handling anywhere. `authSlice` computes
 * `isAuthenticated` from the mere PRESENCE of `admin_token` in localStorage, at
 * store-init time only, so a token that has EXPIRED still reads as signed in:
 * every route renders, every request 401s, and each page was left to invent its
 * own story about it. Two had already been invented, and both were wrong:
 *
 *   - `Billing.tsx` rendered "Your session has expired. Sign in again to see
 *     your subscription." with no button, no link and no navigation. A dead end
 *     that named the fix and then withheld it.
 *   - `MyAvailabilityTab.tsx` folded 401 into its 403 branch and reported a dead
 *     session as "You do not have access to availability in this workspace" —
 *     an access decision that was never made.
 *
 * Patching each page would have produced a third story. The structural fault is
 * that nothing turns "Central refused this credential" into an action, so that
 * is what this module is.
 *
 * SUITE POLICY, applied here
 * --------------------------
 *   401 / dead session  -> recover silently if possible, otherwise initiate
 *                          Central sign-in AUTOMATICALLY via `location.replace`.
 *                          No screen, no button.
 *   403 / access denied -> NOT this module's business. A 403 is an answer, and
 *                          the page should render Access Denied. Callers must
 *                          keep 403 separate; `isDeadSessionStatus` only ever
 *                          says yes to 401.
 */

export const ADMIN_TOKEN_KEY = 'admin_token';

/** Central's canonical sign-in page. NOT `/login`, which is the JWT-paste admin page. */
export const CENTRAL_SIGN_IN_PATH = '/auth/login';
/** Where the bridge sends a multi-workspace user with nothing selected. */
export const CENTRAL_WORKSPACE_PICKER_PATH = '/auth/workspace';

/**
 * Only 401 means "this credential is dead".
 *
 * 403 is deliberately excluded. The two were conflated in both pages this
 * module replaces, and the cost ran in both directions: a dead session was
 * reported as an access refusal, and an access refusal would — under a naive
 * fix — have triggered a sign-in loop that re-authenticates successfully and
 * then 403s again, forever.
 */
export function isDeadSessionStatus(status: number): boolean {
  return status === 401;
}

function currentReturnTo(): string {
  return window.location.pathname + window.location.search;
}

/** Full sign-in URL, carrying the current location so recovery resumes here. */
export function centralSignInUrl(returnTo: string = currentReturnTo()): string {
  return `${CENTRAL_SIGN_IN_PATH}?continue=${encodeURIComponent(returnTo)}`;
}

/**
 * Hand the browser to Central sign-in, now.
 *
 * `location.replace`, NOT `assign`/`href`. The page being left is a page whose
 * session is already dead: leaving it in history means Back returns to it, it
 * re-detects the dead session, and the user is bounced again — the loop that
 * makes an auto-redirect feel broken even when it is correct. There is nothing
 * at this entry worth returning to.
 */
export function beginCentralSignIn(returnTo?: string): void {
  try {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    // Private mode / blocked storage. Navigating still ends the session's use.
  }
  window.location.replace(centralSignInUrl(returnTo));
}

interface BridgeResponse {
  ok?: boolean;
  accessToken?: string;
  needsWorkspaceSelection?: boolean;
}

/** The settings session bridge: the `central_sid` cookie in, an access token out. */
export const SESSION_BRIDGE_PATH = '/api/auth/session/bridge';

/** What the session bridge answered, with no navigation attached. */
export type SessionBridgeOutcome =
  | { readonly kind: 'token'; readonly accessToken: string }
  /** Signed in, but no workspace is bound (several to choose from). */
  | { readonly kind: 'needs-workspace' }
  /** Signed in with an admin-issued temporary password: change it first. */
  | { readonly kind: 'password-change' }
  /** A non-2xx answer: 401 is no session; anything else is Central failing. */
  | { readonly kind: 'refused'; readonly status: number }
  /** A 2xx whose body could not be read. */
  | { readonly kind: 'malformed' }
  /** No answer at all (network failure). */
  | { readonly kind: 'unreachable' };

/**
 * Ask the session bridge for an access token — the request `WorkspaceSettings`
 * makes inline, once, here.
 *
 * It only reports. What each answer MEANS differs by caller: a page that cannot
 * render without a token sends a dead session to sign-in
 * (`recoverAdminSession`), while the post-sign-in landing just falls back to
 * `/home`. Keeping navigation out of here is what lets both share it.
 */
export async function requestSessionBridge(
  fetchImpl: typeof fetch = fetch,
): Promise<SessionBridgeOutcome> {
  let res: Response;
  try {
    res = await fetchImpl(SESSION_BRIDGE_PATH, { credentials: 'include' });
  } catch {
    return { kind: 'unreachable' };
  }
  if (res.status === 403) {
    const refusal = (await res.json().catch(() => null)) as { code?: unknown } | null;
    if (refusal?.code === 'PASSWORD_CHANGE_REQUIRED') return { kind: 'password-change' };
  }
  if (!res.ok) return { kind: 'refused', status: res.status };

  let body: BridgeResponse;
  try {
    body = (await res.json()) as BridgeResponse;
  } catch {
    return { kind: 'malformed' };
  }
  if (body.needsWorkspaceSelection || !body.accessToken) return { kind: 'needs-workspace' };
  return { kind: 'token', accessToken: body.accessToken };
}

/**
 * Store a bridged token where the console, its API client and settings read it.
 * False when storage is blocked: the token is then usable only by the current
 * document, and a page load will not find it.
 */
export function storeAdminToken(token: string): boolean {
  try {
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
    return true;
  } catch {
    return false;
  }
}

/**
 * Try to mint a fresh `admin_token` from the `central_sid` cookie.
 *
 * This is the same bridge-then-login sequence `WorkspaceSettings` already
 * performed inline; it is here so every caller gets it instead of only that one
 * page. The common case is a healthy Central SSO session behind an aged-out
 * access token, which recovers with no interruption at all — the user never
 * learns anything happened, which is the point.
 *
 * Returns the new token, or null when the browser is already being navigated
 * away (sign-in or workspace picker). A null therefore means STOP: do not set
 * state, do not render, the document is going away.
 */
export async function recoverAdminSession(
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  const bridge = await requestSessionBridge(fetchImpl);

  switch (bridge.kind) {
    case 'unreachable':
      // Network failure is NOT evidence that the session is dead, and bouncing
      // to sign-in on it would turn a blip into a logout. Report failure and let
      // the caller surface a retryable error instead.
      return null;
    case 'refused':
    case 'malformed':
      // The cookie is gone or dead too. Sign-in is genuinely the only next step.
      beginCentralSignIn();
      return null;
    case 'password-change':
      // The session is fine; the account must replace a temporary password
      // before Central will issue it any token. Resume here afterwards.
      window.location.replace(`/auth/change-password?continue=${encodeURIComponent(currentReturnTo())}`);
      return null;
    case 'needs-workspace':
      // Authenticated, but no workspace is bound. Picking one for them is how a
      // user ends up silently editing the wrong (oldest) workspace, so the
      // picker owns this. Also a replace: this page cannot be rendered as
      // things stand.
      window.location.replace(
        `${CENTRAL_WORKSPACE_PICKER_PATH}?continue=${encodeURIComponent(currentReturnTo())}`,
      );
      return null;
    case 'token':
      // Unstorable is still usable for this document's lifetime.
      storeAdminToken(bridge.accessToken);
      return bridge.accessToken;
  }
}

/** Read the current admin token, tolerating blocked storage. */
export function readAdminToken(): string | null {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY);
  } catch {
    return null;
  }
}

export interface AuthFetchResult {
  /** Null when the browser is being navigated away — the caller must stop. */
  readonly response: Response | null;
  /** The token actually used, when a recovery replaced it. */
  readonly token?: string;
}

/**
 * Authenticated fetch with one automatic recovery attempt — the interceptor the
 * SPA never had.
 *
 * On 401 it refreshes the token from the session bridge and retries the request
 * exactly ONCE. A second 401 against a freshly minted token is not something
 * another refresh can fix, so it goes to sign-in rather than looping.
 *
 * A null `response` means the document is navigating; callers must return
 * immediately and must not set state (React will warn, and more importantly the
 * user is already gone).
 */
export async function authFetchWithRecovery(
  url: string,
  options: RequestInit = {},
  fetchImpl: typeof fetch = fetch,
): Promise<AuthFetchResult> {
  const token = readAdminToken();
  if (!token) {
    beginCentralSignIn();
    return { response: null };
  }

  const send = (bearer: string): Promise<Response> =>
    fetchImpl(url, {
      ...options,
      headers: { ...(options.headers ?? {}), Authorization: `Bearer ${bearer}` },
    });

  const first = await send(token);
  if (!isDeadSessionStatus(first.status)) return { response: first, token };

  const refreshed = await recoverAdminSession(fetchImpl);
  if (!refreshed) return { response: null };

  const second = await send(refreshed);
  if (isDeadSessionStatus(second.status)) {
    beginCentralSignIn();
    return { response: null };
  }
  return { response: second, token: refreshed };
}

/**
 * End the session properly, then land on Central's canonical sign-in.
 *
 * Clearing local state is NOT a logout. The admin header's "Log out" dispatched
 * `clearToken()` and nothing else, so the `central_sid` SSO cookie survived and
 * the user stayed silently re-authenticatable — by this SPA and by every suite
 * app sharing that session. This is the same defect the shared SDK documents
 * for the Home app, in the identity authority's own front end.
 *
 * Order matters: destroy the server-side session FIRST, and treat a failure as
 * a reason NOT to claim success. Telling someone they are signed out while
 * their session is live is the worst available outcome.
 *
 * Explicit logout keeps `assign` semantics — the user chose to leave, so Back
 * returning to a signed-out app page is ordinary browser behaviour, not a
 * bounce loop.
 */
export async function performCentralAdminLogout(
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const res = await fetchImpl('/api/oidc/session/logout', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Cache-Control': 'no-store' },
  });
  if (!res.ok) {
    throw new Error(
      `Central session logout failed (${res.status}); aborting to avoid an ambiguous signed-out state`,
    );
  }
  try {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    // Non-fatal: the server-side session is already gone.
  }
  window.location.assign(CENTRAL_SIGN_IN_PATH);
}
